import {createAsyncThunk} from '@reduxjs/toolkit';

import deleteEmployeeGroup, {
    type DeleteEmployeeGroupParams,
} from 'api/employee-groups/delete-employee-group';
import {
    type EmployeeGroupResults,
    type EventSlot,
} from 'api/employee-groups/employee-groups.types';
import getEmployeeGroups, {
    GetEmployeeGroupsParams,
} from 'api/employee-groups/get-employee-groups';
import getEventsByCities from 'api/events/get-events-by-cities';
import getEmployeeParkingEventFilterOptions, {
    GetEmployeeParkingEventFilterOptionsRequest,
} from 'api/employee-groups/get-employee-parking-event-filter-options';
import formatEventFilterOptions from 'pages/event-management/utils/format-event-type-options';
import {type RootState} from 'store/store';
import {type EmployeeGroupId} from 'utils/typeid';
import dayjs from 'utils/dayjs';

import {
    employeeParkingGroupsAdapter,
    type EmployeeParkingEvent,
} from './employee-parking-adapters';

/*
 * A Redux thunk to get employee parking groups data.
 *
 * Action Type: employeeParking/getEmployeeGroups
 *
 * API Service: {@link getEmployeeGroups}
 */
export const getEmployeeGroupsData = createAsyncThunk<
    EmployeeGroupResults,
    GetEmployeeGroupsParams
>(
    'employeeParking/getEmployeeGroups',
    async (
        {facilityIds, limit, offset, handleAbortSignal, abortSignal},
        {rejectWithValue, signal}
    ) => {
        try {
            if (handleAbortSignal) {
                handleAbortSignal(signal);
            }

            const response = await getEmployeeGroups(
                facilityIds,
                limit,
                offset,
                {
                    signal: abortSignal,
                }
            );

            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

/*
 * A Redux thunk to delete an employee parking group.
 *
 * Action Type: employeeParking/deleteEmployeeGroupData
 *
 * API Service: {@link deleteEmployeeGroup}
 */
export const deleteEmployeeGroupData = createAsyncThunk<
    string,
    DeleteEmployeeGroupParams
>(
    'employeeParking/deleteEmployeeGroupData',
    async ({employeeGroupId}: DeleteEmployeeGroupParams, {rejectWithValue}) => {
        try {
            await deleteEmployeeGroup({employeeGroupId});

            return employeeGroupId.toString();
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

/*
 * A Redux thunk to get employee parking events data by city IDs.
 *
 * Action Type: employeeParking/getEventsByCities
 *
 * API Service: {@link getEventsByCities}
 */
export const getEmployeeParkingEvents = createAsyncThunk<
    {events: EmployeeParkingEvent[]; meta: {count: number; page: number}},
    {
        cityIds: number[];
        page?: number;
        pageSize?: number;
        groupId?: EmployeeGroupId;
        eventTypeIds?: number[];
        venueIds?: number[];
        searchText?: string;
        fromDate?: string;
        toDate?: string;
        abortSignal?: AbortSignal;
        handleAbortSignal?: (signal: AbortSignal) => void;
    },
    {state: RootState}
>(
    'employeeParking/getEventsByCities',
    async (
        {
            cityIds,
            page = 1,
            pageSize = 25,
            groupId,
            eventTypeIds,
            venueIds,
            searchText,
            fromDate,
            toDate,
            abortSignal,
            handleAbortSignal,
        },
        {getState, rejectWithValue, signal}
    ) => {
        try {
            if (handleAbortSignal) {
                handleAbortSignal(signal);
            }

            const requestData = {
                cityIds,
                page,
                pageSize,
                eventTypeIds,
                venueIds,
                searchText,
                fromDate,
                toDate,
            };

            const response = await getEventsByCities(requestData, {
                signal: abortSignal,
            });
            const groupIdString = groupId?.toString();
            const selectors = employeeParkingGroupsAdapter.getSelectors(
                (state: RootState) => state.employeeParking.groups.data
            );
            const group = groupIdString
                ? selectors.selectById(getState(), groupIdString)
                : null;
            const slotByEventId = new Map<number, EventSlot>();

            group?.eventSlots?.forEach((slot) => {
                slotByEventId.set(slot.eventId, slot);
            });

            const events: EmployeeParkingEvent[] =
                response?.data?.results?.map((event) => ({
                    ...event,
                    ...slotByEventId.get(event.id),
                })) ?? [];

            return {
                events,
                meta: {count: response?.meta?.count ?? 0, page},
            };
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

/**
 * A Redux thunk for fetching employee parking event filter options.
 */
export const getEmployeeParkingEventFilterOptionsData = createAsyncThunk(
    'employeeParking/getEventFilterOptions',
    async (
        {
            cityIds,
            fromDate,
            toDate,
        }: GetEmployeeParkingEventFilterOptionsRequest,
        {rejectWithValue}
    ) => {
        try {
            const response = await getEmployeeParkingEventFilterOptions({
                cityIds,
                fromDate: dayjs(fromDate),
                toDate: dayjs(toDate),
            });

            const formattedEventTypes = formatEventFilterOptions(
                response.eventTypes
            );
            const formattedVenues = formatEventFilterOptions(response.venues);

            return {
                eventTypeIds: formattedEventTypes,
                venueIds: formattedVenues,
            };
        } catch (error) {
            console.error('getEmployeeParkingEventFilterOptionsData error:', {
                error,
            });

            return rejectWithValue(error);
        }
    }
);
